/**
 * AutoStepTracker — ambient in-browser pedometer that runs continuously
 * while the Companion App is open in the foreground.
 *
 * Design & honest limitations:
 *   • PWAs cannot access motion sensors while the tab is backgrounded or
 *     the app is closed. Real 24/7 tracking requires HealthKit / Google
 *     Fit sync (Phase 2b).
 *   • What this DOES give the user: whenever the Companion App is open
 *     (even minimized to a browser tab as long as the OS keeps the tab
 *     alive), we count steps automatically and auto-log them to the
 *     primary active Step-a-Thon event without any button presses.
 *
 * Persistence:
 *   • Session buffer stored in localStorage under `glpflo_autostep_buffer`
 *     as { day: 'YYYY-MM-DD', steps: N, event_slug, last_flush_at }.
 *   • Flushed to backend every 3 min while active, on visibility hidden,
 *     and on beforeunload.
 *
 * Permissions:
 *   • iOS 13+ requires a user gesture to call
 *     DeviceMotionEvent.requestPermission(). We store the user's decision
 *     in localStorage under `glpflo_autostep_enabled` (true/false/null).
 *     If null → show a one-time opt-in row in the Rewards screen.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import API from "@/lib/api";
import { toast } from "sonner";
import { Footprints, Play, PauseCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BUFFER_KEY = "glpflo_autostep_buffer";
const ENABLED_KEY = "glpflo_autostep_enabled";
const PEAK_THRESHOLD = 1.2;   // multiples of g
const COOLDOWN_MS = 250;
const FLUSH_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const MIN_FLUSH_STEPS = 20;               // don't spam backend for a few steps

function today() { return new Date().toISOString().slice(0, 10); }

function readBuffer() {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    if (!raw) return null;
    const buf = JSON.parse(raw);
    if (buf.day !== today()) {
      // Different day → drop the stale buffer.
      localStorage.removeItem(BUFFER_KEY);
      return null;
    }
    return buf;
  } catch { return null; }
}
function writeBuffer(buf) {
  try { localStorage.setItem(BUFFER_KEY, JSON.stringify(buf)); } catch { /* quota */ }
}

/**
 * Renders a tiny status pill + one-time opt-in row. Runs the ambient
 * tracker in the background.
 */
export default function AutoStepTracker({ compact = false }) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(ENABLED_KEY) === "true");
  const [permission, setPermission] = useState("unknown");
  const [steps, setSteps] = useState(() => (readBuffer()?.steps || 0));
  const [running, setRunning] = useState(false);
  const [eventSlug, setEventSlug] = useState(null);
  const lastStepRef = useRef(0);
  const stepsRef = useRef(steps);
  const handlerRef = useRef(null);
  const flushIntervalRef = useRef(null);

  useEffect(() => { stepsRef.current = steps; }, [steps]);

  // Discover the primary active event once — we log all auto-steps to it.
  useEffect(() => {
    axios.get(`${BACKEND}/api/stepathon/events?status=active`)
      .then((r) => {
        const first = (r.data.events || [])[0];
        if (first) setEventSlug(first.slug);
      })
      .catch(() => { /* no active event → tracker still runs, but flushes will be skipped */ });
  }, []);

  const flush = useCallback(async (force = false) => {
    const buf = readBuffer();
    if (!buf || !eventSlug) return;
    if (!force && buf.steps < MIN_FLUSH_STEPS) return;
    if (buf.steps <= 0) return;
    const flushingSteps = buf.steps;
    // Optimistic clear: zero out buffer BEFORE POST so concurrent ticks
    // don't double-count. If POST fails, we lose the batch — acceptable
    // for a pedometer with intrinsic ~85% accuracy anyway.
    writeBuffer({ ...buf, steps: 0, last_flush_at: Date.now() });
    setSteps(0);
    stepsRef.current = 0;
    try {
      await API.post(`/stepathon/events/${eventSlug}/log`, {
        steps: flushingSteps,
        source: "companion",
        session_id: `auto-${buf.day}`,
      });
    } catch (e) {
      // Restore the buffer on failure.
      const cur = readBuffer() || { day: today(), steps: 0, event_slug: eventSlug };
      writeBuffer({ ...cur, steps: (cur.steps || 0) + flushingSteps });
      setSteps((s) => s + flushingSteps);
      stepsRef.current += flushingSteps;
    }
  }, [eventSlug]);

  const start = useCallback(async () => {
    // iOS 13+ requires user-gesture permission.
    const DM = typeof DeviceMotionEvent !== "undefined" && DeviceMotionEvent;
    if (DM && typeof DM.requestPermission === "function") {
      try {
        const res = await DM.requestPermission();
        setPermission(res);
        if (res !== "granted") {
          toast.error("Motion access denied — enable motion access in your device settings to auto-count steps.");
          setEnabled(false);
          localStorage.setItem(ENABLED_KEY, "false");
          return;
        }
      } catch {
        setPermission("denied");
        setEnabled(false);
        localStorage.setItem(ENABLED_KEY, "false");
        return;
      }
    } else {
      setPermission("granted");
    }
    setEnabled(true);
    localStorage.setItem(ENABLED_KEY, "true");
  }, []);

  const stop = useCallback(async () => {
    // Flush anything buffered before disabling.
    await flush(true);
    setEnabled(false);
    localStorage.setItem(ENABLED_KEY, "false");
  }, [flush]);

  // Attach / detach the DeviceMotion handler based on `enabled`.
  useEffect(() => {
    if (!enabled) {
      if (handlerRef.current) {
        window.removeEventListener("devicemotion", handlerRef.current);
        handlerRef.current = null;
      }
      setRunning(false);
      return;
    }
    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity || e.acceleration;
      if (!a) return;
      const g = 9.80665;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2) / g;
      const now = Date.now();
      if (mag > PEAK_THRESHOLD && now - lastStepRef.current > COOLDOWN_MS) {
        lastStepRef.current = now;
        setSteps((s) => {
          const next = s + 1;
          stepsRef.current = next;
          // Persist every step to buffer so a tab close doesn't lose it.
          const buf = readBuffer() || { day: today(), steps: 0, event_slug: eventSlug };
          writeBuffer({ ...buf, day: today(), steps: next, event_slug: eventSlug });
          return next;
        });
      }
    };
    handlerRef.current = onMotion;
    window.addEventListener("devicemotion", onMotion);
    setRunning(true);
    return () => {
      if (handlerRef.current) window.removeEventListener("devicemotion", handlerRef.current);
      handlerRef.current = null;
    };
  }, [enabled, eventSlug]);

  // Periodic flush + on visibility change / beforeunload.
  useEffect(() => {
    if (!enabled || !eventSlug) return;
    flushIntervalRef.current = setInterval(() => { flush(false); }, FLUSH_INTERVAL_MS);
    const onVis = () => { if (document.visibilityState === "hidden") flush(true); };
    const onUnload = () => {
      // beforeunload can't await async, but Fetch keepalive works.
      const buf = readBuffer();
      if (!buf || !buf.steps || buf.steps <= 0) return;
      try {
        const token = localStorage.getItem("token") || "";
        fetch(`${BACKEND}/api/stepathon/events/${eventSlug}/log`, {
          method: "POST",
          keepalive: true,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ steps: buf.steps, source: "companion", session_id: `auto-${buf.day}` }),
        });
        writeBuffer({ ...buf, steps: 0 });
      } catch { /* swallow */ }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      clearInterval(flushIntervalRef.current);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [enabled, eventSlug, flush]);

  // One-time opt-in row for users who never made a choice
  const hasStoredChoice = localStorage.getItem(ENABLED_KEY) !== null;
  if (!hasStoredChoice) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800 p-4" data-testid="companion-autostep-optin">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-900 border border-purple-200 flex items-center justify-center">
            <Footprints className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">Auto-track my steps</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
              Counts steps automatically while the Companion App is open. 1 step = 1 point, no button-mashing.
            </p>
          </div>
          <button
            onClick={start}
            className="rounded-full bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-3 py-1.5 whitespace-nowrap"
            data-testid="companion-autostep-enable-btn"
          >
            Turn on
          </button>
        </div>
      </div>
    );
  }

  if (compact) {
    // Tiny status pill for use inside the points hero
    return (
      <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-white/15 border border-white/20 px-3 py-2" data-testid="companion-autostep-status">
        <div className="flex items-center gap-2 min-w-0">
          <Footprints className={`w-4 h-4 ${running ? "text-white animate-pulse" : "text-white/60"}`} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-90">Auto-tracking</div>
            <div className="text-sm font-bold tabular-nums">{steps.toLocaleString()} steps buffered</div>
          </div>
        </div>
        {enabled ? (
          <button onClick={stop} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider opacity-90 hover:opacity-100" data-testid="companion-autostep-pause-btn">
            <PauseCircle className="w-3 h-3" /> Pause
          </button>
        ) : (
          <button onClick={start} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider opacity-90 hover:opacity-100" data-testid="companion-autostep-resume-btn">
            <Play className="w-3 h-3" /> Resume
          </button>
        )}
      </div>
    );
  }

  return null;
}
